import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, X, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// localStorage key — dismissals are scoped to the calendar day so the banner
// comes back tomorrow if the user still hasn't upgraded.
function _dismissKey() {
  const d = new Date();
  return `tfu_proupsell_dismissed_${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`;
}

export default function ProUpsellBanner({ user, isPro, buildsToday }) {
  const [starting, setStarting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setDismissed(Boolean(window.localStorage.getItem(_dismissKey())));
    }
  }, []);

  // Visibility rules:
  // - Must be signed in (so we can resolve a Stripe customer + trial eligibility)
  // - Must NOT already be Pro
  // - Must have used >=3 builds today
  // - Must not have dismissed for today
  if (!user || isPro || dismissed || buildsToday < 3) return null;

  const dismiss = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(_dismissKey(), '1');
    }
    setDismissed(true);
  };

  const startTrial = async () => {
    setStarting(true);
    try {
      const userKey = window.localStorage.getItem('tfu_session') || user.user_id;
      const res = await axios.post(
        `${API}/checkout/session`,
        {
          package_id: 'pro_monthly',
          origin_url: window.location.origin,
          user_key: userKey,
          trial: true,
        },
        { withCredentials: true },
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      console.error(e);
      setStarting(false);
    }
  };

  return (
    <div
      data-testid="builder-pro-upsell-banner"
      className="relative flex items-center gap-2 px-3 py-2 mx-3 mb-2 rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 text-violet-900"
    >
      <Sparkles className="w-4 h-4 text-violet-600 flex-shrink-0" />
      <p className="text-xs leading-snug flex-1 min-w-0">
        On a roll — <strong>{buildsToday}</strong> builds today. Try{' '}
        <strong>Pro free for 7 days</strong> for Claude-quality code and longer outputs.
      </p>
      <button
        onClick={startTrial}
        disabled={starting}
        data-testid="builder-start-trial-btn"
        className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700 disabled:opacity-60 inline-flex items-center gap-1 flex-shrink-0"
      >
        {starting ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
        Start trial
      </button>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="builder-dismiss-upsell-btn"
        className="text-violet-500 hover:text-violet-800 p-1 rounded transition-colors flex-shrink-0"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
